import React from "react"
import { translate } from "../../translations/translations"
import { useCalculator } from "../../context/CalculatorContext"
import { businessExpenseKeys } from "../../models/ExpenseModel"
import Button from "../Shared/Button"
import { AssetTypes } from "src/models/AssetType"
import styles from "./ResultsDisplay.module.css"

const ResultsDisplay: React.FC<{
  onRestart: () => void
  onBack: () => void
}> = ({ onRestart, onBack }) => {
  const { shipments, expenses, improvements, investment, benefits, reset } =
    useCalculator()

  const handleRestart = () => {
    reset()
    onRestart()
  }

  const assetTypeLabel = AssetTypes.find(
    (type) => type.key === shipments.assetType,
  )?.labelKey

  const assetType = shipments.assetType
  const labels: string[] = assetType
    ? Object.keys(businessExpenseKeys[assetType]).map((key) => {
        const labelKey = (businessExpenseKeys[assetType] as any)[key]
        return translate(labelKey)
      })
    : []

  return (
    <>
      <div className={styles.resultsWrapper}>
        <div className={styles.resultsInner}>
          <div className={styles.resultsContainer}>
            <div className={styles.orangeBackground}>
              <div className={styles.finalRow}>
                <div className={styles.benefitsContainer}>
                  <h3 className={styles.benefitsTitle}>
                    {translate("benefits")}
                  </h3>

                  <div className={styles.resultRow}>
                    <span className={styles.benefitsResultLabel}>
                      {translate("business_improvement")}
                    </span>
                    <span className={styles.benefitsResultValue}>
                      ${benefits?.businessImprovement.toLocaleString()}
                    </span>
                  </div>
                  <div className={styles.resultRow}>
                    <span className={styles.benefitsResultLabel}>
                      {translate("business_improvement_net")}
                    </span>
                    <span className={styles.benefitsResultValue}>
                      ${benefits?.businessImprovementNet.toLocaleString()}
                    </span>
                  </div>
                  <div className={styles.resultRow}>
                    <span className={styles.benefitsResultLabel}>
                      {translate("roi")}
                    </span>
                    <span className={styles.benefitsResultValue}>
                      {benefits?.roiPercentage
                        ? Math.floor(benefits.roiPercentage).toLocaleString()
                        : "0"}
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.resultSection}>
              <h3 className={styles.sectionTitle}>{translate("shipments")}</h3>
              <div className={styles.resultRow}>
                <span className={styles.resultLabel}>
                  {translate("total_yearly_volume")}
                </span>
                <span className={styles.resultValue}>
                  {shipments.yearlyVolume}
                </span>
              </div>
              <div className={styles.resultRow}>
                <span className={styles.resultLabel}>
                  {translate("percentage_to_track")}
                </span>
                <span className={styles.resultValue}>
                  {shipments.percentageToTrack}%
                </span>
              </div>
              <div className={styles.resultRow}>
                <span className={styles.resultLabel}>
                  {translate("number_of_tracked_assets")}
                </span>
                <span className={styles.resultValue}>
                  {shipments.numberOfTrackedAssets}
                </span>
              </div>
              <div className={styles.resultRow}>
                <span className={styles.resultLabel}>
                  {translate("asset_type")}
                </span>
                <span className={styles.resultValue}>
                  {assetTypeLabel
                    ? translate(assetTypeLabel)
                    : translate("unknown")}
                </span>
              </div>
            </div>
            <div className={styles.resultSection}>
              <h3 className={styles.sectionTitle}>
                {translate("smart_label_investment")}
              </h3>
              <div className={styles.resultRow}>
                <span className={styles.resultLabel}>
                  {translate("label_price")}
                </span>
                <span className={styles.resultValue}>
                  ${investment.labelPrice}
                </span>
              </div>
              {/* <div className={styles.resultRow}>
                                    <span className={styles.resultLabel}>
                                        {translate("cloud_subscription")}
                                    </span>
                                    <span className={styles.resultValue}>
                                        ${investment.cloudSubscription}
                                    </span>
                                </div> */}
              <div className={styles.resultRow}>
                <span className={styles.resultLabel}>
                  {translate("annual_investment")}
                </span>
                <span className={styles.resultValue}>
                  ${investment.annualInvestment?.toLocaleString()}
                </span>
              </div>
              <div className={styles.resultRow}>
                <span className={styles.resultLabel}>
                  {translate("label_lifecycle_years")}
                </span>
                <span className={styles.resultValue}>
                  {investment.labelLifecycle}
                </span>
              </div>
            </div>

            <div className={styles.resultSection}>
              <h3 className={styles.sectionTitle}>
                {translate("business_expense")}
              </h3>
              {Object.entries(expenses.expenses).map(([key, value], index) => (
                <div className={styles.resultRow} key={key}>
                  <span className={styles.resultLabel}>{labels[index]}</span>
                  <span className={styles.resultValue}>
                    ${value.toLocaleString()}
                  </span>
                </div>
              ))}
              <div className={styles.resultRow}>
                <span className={styles.resultLabel}>
                  {translate("total_business_loss")}
                </span>
                <span className={styles.resultValue}>
                  ${expenses.totalBusinessLoss?.toLocaleString()}
                </span>
              </div>
            </div>
            <div className={styles.resultSection}>
              <h3 className={styles.sectionTitle}>
                {translate("business_improvement")}
              </h3>
              {improvements.improvementAmounts &&
                Object.entries(improvements.improvementAmounts).map(
                  ([key, amount], index) => (
                    <div className={styles.resultRow} key={key}>
                      <span className={styles.resultLabel}>
                        {labels[index]}
                      </span>
                      <span className={styles.resultValue}>
                        ${amount.toLocaleString()}
                      </span>
                    </div>
                  ),
                )}
              <div className={styles.resultRow}>
                <span className={styles.resultLabel}>
                  {translate("total_savings")}
                </span>
                <span className={styles.resultValue}>
                  ${improvements.totalSavings?.toLocaleString()}
                </span>
              </div>
            </div>

            <div className={`${styles.restartButtonContainer}`}>
              <Button onClick={onBack} label={translate("back")} />
              <Button onClick={handleRestart} label={translate("restart")} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ResultsDisplay
